"use client"

import { useEffect, useMemo, useState } from "react"
import {
  getCountryConfigStatic,
  publicCountries,
  type Country,
  type CountryConfig,
} from "@/i18n/config"

/**
 * Country codes whose preview gate maps to a non-httpOnly companion
 * cookie. Set in `proxy.ts` alongside the httpOnly token cookie when a
 * previewer is admitted, so client-side surfaces can detect "this visitor
 * is in preview mode for <market>" without seeing the token value.
 *
 * Cookie name shape: `<key>-preview-active=1` (e.g. `de-preview-active`,
 * `nl-preview-active`, `fr-preview-active`). The mapping is duplicated
 * here intentionally — proxy.ts can't be imported into client code
 * (edge-runtime-only deps), and the names are stable contract.
 */
const PREVIEW_COOKIE_BY_COUNTRY: Record<Country, string> = {
  ch: "fr-preview-active", // CH-FR is the only gated CH locale
  nl: "nl-preview-active",
  de: "de-preview-active",
  at: "at-preview-active",
}

function readPreviewCookieFlags(): Set<Country> {
  if (typeof document === "undefined") return new Set()
  const cookies = document.cookie.split(";")
  const active = new Set<Country>()
  for (const raw of cookies) {
    const [rawName, rawValue] = raw.split("=")
    if (!rawName || !rawValue) continue
    const name = rawName.trim()
    const value = rawValue.trim()
    if (value !== "1") continue
    for (const [country, cookieName] of Object.entries(
      PREVIEW_COOKIE_BY_COUNTRY
    )) {
      if (name === cookieName) active.add(country as Country)
    }
  }
  return active
}

/**
 * Returns the set of country codes the current visitor has an active
 * preview cookie for. Empty during SSR / before hydration so the server
 * HTML matches `publicCountries` exactly — preventing hydration
 * mismatches and keeping Googlebot's view unchanged.
 *
 * Used by the locale picker so a DE previewer on `/ch/en` can still
 * see the DE option, and so a CH-FR previewer who switched away can
 * find their way back.
 */
export function usePreviewCountries(): Set<Country> {
  const [countries, setCountries] = useState<Set<Country>>(() => new Set())
  useEffect(() => {
    setCountries(readPreviewCookieFlags())
  }, [])
  return countries
}

/**
 * Returns the country list the locale picker should render.
 *
 * - Default: `publicCountries` (LAUNCHED markets only). Matches the
 *   server-rendered HTML so Googlebot and anonymous visitors see no
 *   leak of unlaunched markets.
 * - Widened on the client when affirmative preview signal exists:
 *   1. A `<key>-preview-active` cookie is present, OR
 *   2. The visitor is already on a non-public country's route (they
 *      reached it through some other admit path — at minimum let them
 *      navigate within the markets they've discovered).
 *
 * The CH-FR case is special: it's not a separate country, but a gated
 * language on CH. When the `fr-preview-active` cookie is set we widen
 * CH's `languages` to include `fr` (it might already be there if
 * FR_LAUNCHED=true; `publicCountries` handles that).
 *
 * Returned list is ordered the same way `publicCountries` is, with
 * any preview-only markets appended after.
 */
export function usePickerCountries(currentCountry: Country): CountryConfig[] {
  const previewCountries = usePreviewCountries()

  return useMemo(() => {
    // Widen CH's languages if the FR preview cookie is active and CH's
    // current `languages` doesn't already include "fr" (i.e. pre-launch).
    const result: CountryConfig[] = publicCountries.map((cc) => {
      if (cc.code !== "ch") return cc
      if (!previewCountries.has("ch")) return cc
      if (cc.languages.includes("fr")) return cc
      return { ...cc, languages: [...cc.languages, "fr"] }
    })

    // Append any non-public country the visitor has affirmative signal for.
    // Order: visitor's current country first (highest discoverability), then
    // any others. Use `getCountryConfigStatic` so we don't depend on the
    // build-time `*_ENABLED` flag for lookups — the static config is the
    // shape of the country regardless of whether routes are live.
    const candidates = new Set<Country>(previewCountries)
    if (currentCountry && !result.some((c) => c.code === currentCountry)) {
      candidates.add(currentCountry)
    }
    // CH widening was handled above; the country itself is already in `result`.
    candidates.delete("ch")

    const orderedExtras: Country[] = []
    if (candidates.has(currentCountry)) {
      orderedExtras.push(currentCountry)
      candidates.delete(currentCountry)
    }
    for (const c of candidates) orderedExtras.push(c)

    for (const code of orderedExtras) {
      if (result.some((c) => c.code === code)) continue
      const config = getCountryConfigStatic(code)
      if (config) result.push(config)
    }

    return result
  }, [previewCountries, currentCountry])
}
